import { customAlphabet } from 'nanoid'

/**
 * Stable short id generator (URL-safe, 12 chars).
 */
const alphabet = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
const nano = customAlphabet(alphabet, 12)

export function createId(): string {
  return nano()
}

/**
 * Structured clone fallback. Prefer native `structuredClone` when available,
 * otherwise fall back to JSON round-trip (good enough for our plain AST).
 */
export function deepClone<T>(value: T): T {
  if (typeof globalThis.structuredClone === 'function') {
    return globalThis.structuredClone(value)
  }
  return JSON.parse(JSON.stringify(value))
}

/**
 * Parse a raw input string (from a TagInput paste/keydown) into a list of
 * trimmed, deduplicated tag strings. Rules:
 *   - split by `,`, newline, or tab
 *   - trim each piece
 *   - drop empty pieces
 *   - dedupe (case-sensitive; prompts are often case-sensitive)
 */
export function parseTagsInput(raw: string): string[] {
  if (!raw)
    return []
  const pieces = raw
    .split(/[,\n\t]+/)
    .map(p => p.trim())
    .filter(Boolean)
  const seen = new Set<string>()
  const out: string[] = []
  for (const p of pieces) {
    if (!seen.has(p)) {
      seen.add(p)
      out.push(p)
    }
  }
  return out
}

/**
 * Clamp a number into [min, max]. NaN-safe: returns `min` for NaN.
 */
export function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n))
    return min
  return Math.min(max, Math.max(min, n))
}

/**
 * Non-empty string check (trims first).
 */
export function isNonEmpty(s: string | undefined | null): s is string {
  return typeof s === 'string' && s.trim().length > 0
}

/**
 * Join strings with a separator, skipping empty/undefined pieces.
 */
export function joinNonEmpty(pieces: Array<string | undefined | null>, sep: string): string {
  return pieces.filter(isNonEmpty).join(sep)
}
